import { Component, ViewChild } from '@angular/core';

import { ModalController, ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { combineLatest, firstValueFrom, Subscription } from 'rxjs';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { PowerReviewService } from '@shared/services/power-review.service';
import { ListsOverlayService } from '@app/lists/services/lists-overlay.service';
import { ListsService } from '@app/lists/services/lists.service';
import { SelectionType } from '@app/new-search/models/selection-type.enum';
import { delay, distinctUntilChanged, filter, take } from 'rxjs/operators';
import { BrowserPageTitle } from '../../../shared/constants/browser-page-title.enum';
import { UserService } from '@app/user/services';
import { TranslateService } from '@ngx-translate/core';
import { SearchResultViewModel } from '@search/shared/models/search-view.model';
import { AddToListModalService } from '@shared/services/dual-pane/add-to-list-modal.service';
import { SubstitutionTypeEnum } from '@shared/constants/substitution-type-enum';
import { ProductService } from '@shared/services/product/product.service';
import { Location } from '@angular/common';
import { FacetFlyoutContainerComponent } from '@app/new-search/components/facet-flyout-container/facet-flyout-container.component';
import { NewSearchBasePage } from '@app/new-search/shared/new-search-base.page';
import { ModalService } from '@shared/services/modal/modal.service';
import { ProductExceptionPopoverComponent } from '@search/components/product-exception-popover/product-exception-popover.component';
import { UsfProductCardModeEnum } from '@shared/constants/usf-product-card-mode.enum';
import { environment } from '../../../../environments/environment';
import { Params } from '@angular/router';
import { PATHS } from '@shared/constants/paths';
import { RecipeDetailService } from '@app/mpp/pages/recipe-detail/services/recipe-detail.service';

@Component({
  selector: 'app-substitutes',
  templateUrl: './substitutes.page.html',
  styleUrls: ['./substitutes.page.scss'],
})
export class SubstitutesPage
  extends NewSearchBasePage
  implements ViewWillEnter, ViewWillLeave
{
  substitutionType: SubstitutionTypeEnum =
    SubstitutionTypeEnum.substituteSearch;
  replacementBaseProduct: SearchResultViewModel;

  searchResult$: Subscription;
  userQueryParamSub$: Subscription;
  isSubstitution: boolean;

  @ViewChild(FacetFlyoutContainerComponent)
  facetFlyout: FacetFlyoutContainerComponent;

  shouldDisplayAddToList = false;
  headerText: string;
  footerActionText: string;

  constructor(
    readonly platformService: PlatformService,
    public powerReviewService: PowerReviewService,
    readonly panAppState: PanAppState,
    public listsOverlayService: ListsOverlayService,
    public listsService: ListsService,
    readonly userService: UserService,
    public location: Location,
    public addToListModalService: AddToListModalService,
    readonly productService: ProductService,
    protected modalController: ModalController,
    private modalService: ModalService,
    readonly recipeDetailService: RecipeDetailService,
  ) {
    super(
      listsOverlayService,
      addToListModalService,
      listsService,
      productService,
      location,
      recipeDetailService,
      userService,
    );
  }

  public goBack() {
    this.navigationHelperService.redirectionBack();
  }

  async ionViewWillEnter() {
    this.buildListFacet();
    await this.configureCoveoContext();
    this.headerText = this.translateService.instant('i18n.search.replace');
    this.initializeCoveo({ initializeSearchBoxController: true });
    const customization$ = this.userService.userCustomizations$().pipe(
      filter(value => !!value),
      distinctUntilChanged(),
    );
    const queryParam$ = this.route.queryParams.pipe(distinctUntilChanged());

    this.userQueryParamSub$ = combineLatest([
      customization$,
      queryParam$,
    ]).subscribe(([customization, params]) => {
      this.setUpParams(params);
      this.isAvendraUser = customization?.profileName === 'Avendra';
      const { originalProduct, substitutionType } = params;
      if (this.originSearchPage) {
        this.setGoBackText();
      }
      // keep the order of the following two statements
      this.manageSubstitutionType(substitutionType);
      this.setBaseProduct(+originalProduct);
      // this will reset the selectedProducts array
      this.summary?.state?.firstRequestExecuted &&
        this.parameterManagerController?.synchronize(
          this.convertParamsToCoveo(params),
        );
    });
    this.isTouchExperience$ = this.platformService.isTouchExperience$.subscribe(
      isTouchExperience => {
        this.isTouchExperience = isTouchExperience;
      },
    );
    this.listPageDataSub$ = this.listsService.listsView$().subscribe(data => {
      this.listPageData = data;
    });
    this.observeToggleState();
    this.panAppState.setPageTitle(BrowserPageTitle.searchForSub);
  }

  ionViewWillLeave(): void {
    this.userQueryParamSub$?.unsubscribe();
    this.searchResult$?.unsubscribe();
    this.isTouchExperience$?.unsubscribe();
    this.listPageDataSub$?.unsubscribe();
    this.toggleStateSub$.unsubscribe();
    this.firstLoadPerformed = false;
    this.showInventory = undefined;
    this.clearSearchSub$?.unsubscribe();
    this.isGuestUser$.unsubscribe();
    this.showInventory$?.unsubscribe();
    this.coveoTeardown();
    this.coveoClickEventSub$?.unsubscribe();
    this.currentSearchTerm = '';
    this.defaultSelectionType = undefined;
    this.shouldDisplayAddToList = false;
    this.footerActionText = '';
    this.clearCoveoContext();
  }

  manageSubstitutionType(substitutionType: SubstitutionTypeEnum) {
    this.substitutionType = substitutionType;
    switch (substitutionType) {
      case SubstitutionTypeEnum.replacementSearch: {
        this.defaultSelectionType = SelectionType.AddToList;
        this.selection = this.selection || this.defaultSelectionType;
        this.footerActionText = this.headerText;
        break;
      }
      case SubstitutionTypeEnum.exceptionSearch: {
        this.defaultSelectionType = SelectionType.AddToList;
        this.selection = this.selection || this.defaultSelectionType;
        this.headerText = this.translateService.instant(
          'i18n.search.substitute',
        );
        this.footerActionText = this.headerText;
        break;
      }
      case SubstitutionTypeEnum.substituteSearch: {
        this.shouldDisplayAddToList = true;
        this.headerText = this.translateService.instant(
          'i18n.search.substitute',
        );
        this.footerActionText = this.translateService.instant(
          'i18n.search.chooseList',
        );
        break;
      }
    }
  }

  openFacetFlyout(facetId?: string) {
    this.facetFlyout.openFlyout(facetId);
  }

  async openReplaceProductModal() {
    const listPageData = await firstValueFrom(this.listsService.listsView$());
    const ret = this.listsOverlayService.openReplaceProductModal(
      this.replacementBaseProduct?.productNumber,
      this.selectedProducts[0].productNumber,
      listPageData?.lastSelectedList,
    );
    (await ret).onDidDismiss().then(res => {
      if (res.data?.didSubmit) {
        this.clearSelectedProducts();
      }
    });
  }

  async openProductExceptionPopover() {
    let exceptionId = '';
    this.route.queryParamMap.pipe(take(1)).subscribe(queryParamsMap => {
      exceptionId = queryParamsMap.get('exceptionId');
    });

    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      ProductExceptionPopoverComponent,
      {
        product: this.replacementBaseProduct,
        replacementProduct: this.selectedProducts[0],
        quantityEach: {
          previousValue: 0,
          currentValue: 0,
          loading: false,
          error: null,
        },
        quantityUnit: {
          previousValue: 0,
          currentValue: 0,
          loading: false,
          error: null,
        },
        exceptionId,
        // doesn't look like need, to double check before removing
        mode: UsfProductCardModeEnum.exceptionSearch,
      },
      'modal-desktop-size-lg',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    modal.onDidDismiss().then(payload => {
      if (payload.data?.success) {
        this.location.back();
      }
    });
    await modal.present();
  }

  async openActionModal() {
    if (this.substitutionType === SubstitutionTypeEnum.replacementSearch) {
      await this.openReplaceProductModal();
    } else if (this.substitutionType === SubstitutionTypeEnum.exceptionSearch) {
      await this.openProductExceptionPopover();
    } else {
      this.openAddToListModal();
    }
  }

  async configureCoveoContext() {
    const params: Params = this.navigationHelperService.getParamAsObject();
    this.coveoService.setCustomContext('search_substitution', true);
    this.coveoService.setCustomContext(
      'excludedProduct',
      params?.originalProduct,
    );
    const context = await this.coveoService.getContext();
    context.setView({
      url: `https://${environment.webDomain}/${PATHS.NEW_SEARCH_SUBSTITUTES}`,
    });
  }

  async clearCoveoContext() {
    this.coveoService.clearCustomContext();
    const context = await this.coveoService.getContext();
    context.setView({
      url: `https://${environment.webDomain}/${PATHS.NEW_SEARCH}`,
    });
  }
}
